'use client'

import { useEffect } from 'react'

/**
 * FRIDGE-1091 (owner, on a phone: "Does not rotate on Mobile" / "Not rotating like desktop";
 * docs/Design.md §22.3 — rotates on the phone too, a touch pauses, a swipe takes over,
 * releasing resumes, dots show the position, reduced motion stops it).
 *
 * The rotation itself is the same CSS the desktop hero runs (canvas.css `rotfade`, 7.5 s,
 * three slides 2.5 s apart); this hook only listens on the phone card:
 *   - pointerdown anywhere on the card → `.is-held` (animations paused) until 4 s after release;
 *   - a horizontal swipe, or a dot tap → `.is-manual` + `data-active=<n>`: the chosen slide
 *     shows, the CSS loop is off, and the loop resumes 6 s after the last gesture;
 *   - nothing runs under prefers-reduced-motion (the stylesheet already pins slide 1).
 * No React state: three class writes on one element.
 */
export const HERO_HOLD_MS = 4000
export const HERO_MANUAL_MS = 6000

export function HeroMobileMotion() {
  useEffect(() => {
    const card = document.querySelector<HTMLElement>('.hero-rotator[data-slides]')
    if (!card || matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const count = Number(card.dataset.slides || 1)
    if (count < 2) return
    let pointer: number | null = null, startX = 0, startY = 0, swiped = false, holdTimer = 0, manualTimer = 0
    // The slide on screen: the chosen one in manual mode, otherwise whichever the CSS loop is
    // showing right now (highest opacity) — so a swipe always moves from what the thumb sees.
    const active = () => {
      if (card.dataset.active) return Number(card.dataset.active)
      let best = 0, max = -1
      card.querySelectorAll<HTMLElement>('.hero-mobile-summary .hero-mobile-slide').forEach((el, i) => {
        const o = parseFloat(getComputedStyle(el).opacity)
        if (o > max) { max = o; best = i }
      })
      return best
    }
    const show = (n: number) => {
      card.dataset.active = String(((n % count) + count) % count)
      card.classList.add('is-manual')
      window.clearTimeout(manualTimer)
      manualTimer = window.setTimeout(() => { card.classList.remove('is-manual'); delete card.dataset.active }, HERO_MANUAL_MS)
    }
    const down = (e: PointerEvent) => {
      if (!e.isPrimary) return
      pointer = e.pointerId; startX = e.clientX; startY = e.clientY; swiped = false
      window.clearTimeout(holdTimer); card.classList.add('is-held')
    }
    const move = (e: PointerEvent) => {
      if (pointer !== e.pointerId || swiped) return
      const dx = e.clientX - startX, dy = e.clientY - startY
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) { swiped = true; show(active() + (dx < 0 ? 1 : -1)) }
    }
    const up = (e: PointerEvent) => {
      if (pointer !== e.pointerId) return
      pointer = null
      holdTimer = window.setTimeout(() => card.classList.remove('is-held'), HERO_HOLD_MS)
    }
    const dot = (e: Event) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>('button[data-dot]')
      if (b) { e.preventDefault(); show(Number(b.dataset.dot)) }
    }
    // Switching layouts starts animations in the newly visible price panel,
    // while the shared photo keeps its old clock. Restart them together so
    // a photograph never represents a different product from its prices.
    const phone = matchMedia('(max-width: 560px)')
    let syncFrame = 0
    const syncLayout = () => {
      window.clearTimeout(holdTimer); window.clearTimeout(manualTimer)
      card.classList.remove('is-held', 'is-manual'); delete card.dataset.active
      window.cancelAnimationFrame(syncFrame)
      syncFrame = window.requestAnimationFrame(() => {
        const startTime = document.timeline.currentTime
        if (startTime === null) return
        card.getAnimations({ subtree: true }).forEach(animation => {
          if ('animationName' in animation && animation.animationName === 'rotfade') {
            animation.startTime = startTime
          }
        })
      })
    }
    phone.addEventListener('change', syncLayout)
    card.addEventListener('pointerdown', down)
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    card.addEventListener('click', dot)
    return () => {
      window.clearTimeout(holdTimer); window.clearTimeout(manualTimer)
      window.cancelAnimationFrame(syncFrame)
      phone.removeEventListener('change', syncLayout)
      card.removeEventListener('pointerdown', down); window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up); window.removeEventListener('pointercancel', up); card.removeEventListener('click', dot)
      card.classList.remove('is-held', 'is-manual'); delete card.dataset.active
    }
  }, [])
  return null
}
